// Completing an inward operator task is a UI-only decision: `PUT /PickStation/completeInwardTask/{id}`
// is sent by exactly one place in the system, the Material Inward screen, once it observes the
// submitted tote leave its gate's processing queue. That observation lives in component memory
// (`monitoredSlotsRef`), so leaving the screen while a tote is in flight throws it away, and the
// remount recovery can only re-adopt totes still sitting at a gate. A tote the bot has already
// taken is stranded: its operator task stays pending forever and the orchestration step behind it
// never completes.
//
// Seen in e3d on 2026-08-18 (fulfillment FID20260818100714103): the operator alternated between
// the picking screen and Material Inward roughly every 40 s while running an in-flight inward, and
// four of six totes — TOTEID049/050/051/052 — left their gate during a window when the screen was
// unmounted. All four had to be completed by hand through Swagger ~90 minutes later.
//
// The authoritative list `GET /PickStation/inwardTasks` is already polled by App for the badge
// count, so the same answer can be rebuilt from server state instead of being remembered.

export interface InwardGateLocation {
  x?: number;
  y?: number;
  z?: number;
}

export interface ReconcilableInwardTask {
  id?: string;
  isComplete?: boolean;
  inwardTask?: {
    binId?: string | null;
    from?: InwardGateLocation | null;
    forStaticPickWall?: boolean | null;
  } | null;
}

export interface ReconcilableGate {
  slot?: InwardGateLocation | null;
  processingQueue?: Array<{ binId?: string | null }> | null;
}

export interface ReconcilableStation {
  gates?: ReconcilableGate[] | null;
}

export interface StrandedInwardTask {
  operatorTaskId: string;
  binId: string;
}

export interface FindStrandedInwardTasksOptions {
  /** Bins the live monitor already owns — it will complete those itself. */
  ignoreBinIds?: Iterable<string>;
  /** Tasks with a completion already in flight, so a slow PUT is not sent twice. */
  ignoreTaskIds?: Iterable<string>;
}

// The API rejects a second completion (400 "already complete", or "already being completed by
// another operation" when two callers race the ETag claim in ClaimTaskForCompletionAsync). Both
// mean the task reached the state we wanted, so neither is an error worth showing an operator.
const ALREADY_COMPLETE_PATTERN = /already (complete|being completed)/;

export function normalizeBinId(binId: string | undefined | null): string {
  return (binId ?? "").trim();
}

export function isAlreadyCompleteError(error: any): boolean {
  const status = error?.response?.status;
  const data = error?.response?.data;
  const message =
    typeof data === "string"
      ? data
      : data?.message ?? data?.title ?? error?.message ?? "";

  return status === 400 && ALREADY_COMPLETE_PATTERN.test(message.toLowerCase());
}

function gateKey(location: InwardGateLocation | null | undefined): string | null {
  if (
    typeof location?.x !== "number" ||
    typeof location?.y !== "number" ||
    typeof location?.z !== "number"
  ) {
    return null;
  }

  return `${location.x}.${location.y}.${location.z}`;
}

/**
 * Pending inward tasks whose tote is no longer at any inward gate — the bot has taken it, so the
 * store is done and the task should have been completed.
 *
 * Deliberately conservative: absence of evidence is only treated as evidence of absence when the
 * snapshot can actually speak for the gate in question.
 *
 * - No station snapshot at all → nothing is stranded. An empty list is "we don't know", not
 *   "no tote is at a gate".
 * - Static pick wall tasks are skipped: static gates never carry a processing queue, so "not in a
 *   queue" is true from the moment the task is created and would auto-complete unplaced totes.
 * - A task whose gate is absent from the snapshot is skipped for the same reason.
 *
 * The caller is expected to confirm the tote really is stored (bin `slotAssigned`) before acting —
 * this only narrows the field to candidates worth asking about.
 */
export function findStrandedInwardTasks(
  tasks: ReconcilableInwardTask[] | null | undefined,
  stations: ReconcilableStation[] | null | undefined,
  options: FindStrandedInwardTasksOptions = {}
): StrandedInwardTask[] {
  if (!Array.isArray(tasks) || tasks.length === 0) {
    return [];
  }

  if (!Array.isArray(stations) || stations.length === 0) {
    return [];
  }

  const knownGateKeys = new Set<string>();
  const binsAtGates = new Set<string>();

  stations.forEach((station) => {
    (station?.gates ?? []).forEach((gate) => {
      const key = gateKey(gate?.slot);
      if (key) {
        knownGateKeys.add(key);
      }

      (gate?.processingQueue ?? []).forEach((item) => {
        const binId = normalizeBinId(item?.binId);
        if (binId) {
          binsAtGates.add(binId);
        }
      });
    });
  });

  if (knownGateKeys.size === 0) {
    return [];
  }

  const ignoredBinIds = new Set(
    [...(options.ignoreBinIds ?? [])].map((binId) => normalizeBinId(binId))
  );
  const ignoredTaskIds = new Set(options.ignoreTaskIds ?? []);

  const stranded = new Map<string, StrandedInwardTask>();

  tasks.forEach((task) => {
    const operatorTaskId = (task?.id ?? "").trim();
    if (!operatorTaskId || task?.isComplete === true) {
      return;
    }

    const inwardTask = task?.inwardTask;
    if (!inwardTask || inwardTask.forStaticPickWall === true) {
      return;
    }

    const binId = normalizeBinId(inwardTask.binId);
    if (!binId) {
      return;
    }

    if (ignoredTaskIds.has(operatorTaskId) || ignoredBinIds.has(binId)) {
      return;
    }

    const key = gateKey(inwardTask.from);
    if (!key || !knownGateKeys.has(key)) {
      return;
    }

    if (binsAtGates.has(binId)) {
      return;
    }

    stranded.set(operatorTaskId, { operatorTaskId, binId });
  });

  return [...stranded.values()];
}
